import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PhoneInput from './PhoneInput';
import { phoneDigits } from './phone';
import { uc } from '../unifiedContactsApi';
import { toast } from '../../hooks/use-toast';

// Minimal inline "create a contact" form (ticket 75). Used where a picker
// needs a "+ New contact" path without leaving the page — e.g. the loan
// file's Attach contact. Creates through POST /v2/contacts and hands the new
// record to onCreated so the caller can attach it.
// Ticket 155: `initial` prefills the fields. When `onExisting` is passed, the
// typed email, then phone, is checked against existing contacts first; a hit
// shows "Already a contact" instead of creating a duplicate, and "Use them"
// hands that contact to onExisting.
const norm = (v) => String(v || '').trim().toLowerCase();

export default function QuickContactForm({ defaultType = 'borrower', onCreated, onCancel, onExisting, initial, testIdPrefix = 'quick-contact' }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  // Exact match on normalized email / phone digits, never a name match.
  const findExisting = async (f) => {
    if (f.email) {
      const r = await uc.search(f.email, 8);
      const hit = (r?.contacts || []).find((c) => (c.emails || []).some((e) => norm(e.value) === norm(f.email)));
      if (hit) return hit;
    }
    if (phoneDigits(f.phone).length === 10) {
      const r = await uc.search(phoneDigits(f.phone), 8);
      const hit = (r?.contacts || []).find((c) => (c.phones || []).some((p) => phoneDigits(p.value) === phoneDigits(f.phone)));
      if (hit) return hit;
    }
    return null;
  };
  // A changed email or phone makes an earlier "Already a contact" hit stale.
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (k === 'email') setExisting(null);
  };

  const save = async () => {
    const f = { ...form };
    Object.keys(f).forEach((k) => { f[k] = f[k].trim(); });
    if (!f.first_name && !f.last_name && !f.email && !f.phone) {
      toast({ title: 'Enter at least a name, email, or phone' });
      return;
    }
    setSaving(true);
    try {
      if (onExisting) {
        let hit;
        try {
          hit = await findExisting(f);
        } catch (e) {
          toast({ title: 'Could not check for existing contacts', description: e?.response?.data?.detail || String(e) });
          return;
        }
        if (hit) { setExisting(hit); return; }
      }
      const created = await uc.create({
        first_name: f.first_name || null,
        last_name: f.last_name || null,
        contact_type: defaultType,
        emails: f.email ? [{ value: f.email, is_primary: true }] : [],
        phones: f.phone ? [{ value: f.phone, is_primary: true }] : [],
      });
      toast({ title: 'Contact created', description: created.display_name });
      onCreated?.(created);
    } catch (e) {
      toast({ title: 'Create failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid={testIdPrefix}>
      <div className="text-[12px] font-semibold" style={{ color: '#070B5F' }}>New contact</div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs text-slate-500">First name</Label><Input value={form.first_name} onChange={set('first_name')} data-testid={`${testIdPrefix}-first`} /></div>
        <div><Label className="text-xs text-slate-500">Last name</Label><Input value={form.last_name} onChange={set('last_name')} data-testid={`${testIdPrefix}-last`} /></div>
        <div><Label className="text-xs text-slate-500">Email</Label><Input type="email" value={form.email} onChange={set('email')} data-testid={`${testIdPrefix}-email`} /></div>
        <div><Label className="text-xs text-slate-500">Phone</Label><PhoneInput value={form.phone} onChange={(d) => { setForm((f) => ({ ...f, phone: d })); setExisting(null); }} data-testid={`${testIdPrefix}-phone`} /></div>
      </div>
      {existing && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 flex items-center justify-between gap-2" data-testid={`${testIdPrefix}-existing`}>
          <span>Already a contact: <b>{existing.display_name}</b> — use them?</span>
          <span className="flex gap-1 shrink-0">
            <button type="button" onClick={() => onExisting(existing)}
              className="h-7 px-2 font-semibold rounded-md text-white" style={{ background: '#070B5F' }} data-testid={`${testIdPrefix}-use-existing`}>
              Use them
            </button>
            <button type="button" onClick={() => setExisting(null)}
              className="h-7 px-2 font-semibold rounded-md text-slate-600 hover:bg-amber-100" data-testid={`${testIdPrefix}-existing-cancel`}>
              Cancel
            </button>
          </span>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving}
          className="h-8 px-3 text-[12px] font-semibold rounded-lg text-slate-600 hover:bg-slate-100" data-testid={`${testIdPrefix}-cancel`}>
          Cancel
        </button>
        <button type="button" onClick={save} disabled={saving}
          className="h-8 px-3 text-[12px] font-semibold rounded-lg text-white flex items-center gap-1" style={{ background: '#070B5F' }} data-testid={`${testIdPrefix}-save`}>
          {saving && <Loader2 className="animate-spin" size={12} />} Create & attach
        </button>
      </div>
    </div>
  );
}
